import { Helmet } from 'react-helmet-async';
import { GOOGLE_MAPS_CONFIG } from '../config/google-maps';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
}

const CLINIC_NAME = 'Manomay Homeopathy and Counselling Center';
const CLINIC_URL = 'https://www.manomayhomeopathy.com/';
const CLINIC_ID = `${CLINIC_URL}#medicalclinic`;
const CLINIC_PHONE = '+918169782983';
const CLINIC_IMAGE = 'https://storage.googleapis.com/manomay-homeopathy/miscellaneous/manomay-wallpaper.webp';

/**
 * Official clinic address
 *
 * Human-readable version:
 * Office No. 515, 5th Floor, Fortuna Business Center, Shivar Chowk,
 * Above Hotel Radha Krishna, Opposite McDonald's, Pimple Saudagar,
 * Pimpri-Chinchwad, Pune, Maharashtra 411027, India
 */
const CLINIC_ADDRESS = {
  '@type': 'PostalAddress',
  streetAddress: "Office No. 515, 5th Floor, Fortuna Business Center, Shivar Chowk, Above Hotel Radha Krishna, Opposite McDonald's, Pimple Saudagar",
  addressLocality: 'Pimpri-Chinchwad',
  addressRegion: 'Maharashtra',
  postalCode: '411027',
  addressCountry: 'IN'
};

const CLINIC_GEO = {
  '@type': 'GeoCoordinates',
  latitude: 18.5955388,
  longitude: 73.7878762
};

export default function SEO({
  title = CLINIC_NAME,
  description =
  'Manomay Homeopathy and Counselling Center in Pimple Saudagar, Pimpri-Chinchwad offers homeopathy consultations, counselling and mental health services with Dr. Samiksha Murkute.',
  image = CLINIC_IMAGE,
  url = CLINIC_URL
}: SEOProps) {

  /**
   * Keep canonical URLs consistent.
   * Example:
   * /about -> /about/
   */
  const canonicalUrl = url.endsWith('/') ? url : `${url}/`;

  /**
   * Main MedicalClinic structured data
   */
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalClinic',

    /**
     * Stable identifier for the clinic entity.
     * Do not change this based on individual page URLs.
     */
    '@id': CLINIC_ID,

    name: CLINIC_NAME,
    url: CLINIC_URL,
    image: [image],
    description,
    telephone: CLINIC_PHONE,
    address: CLINIC_ADDRESS,
    geo: CLINIC_GEO,
    medicalSpecialty: 'https://schema.org/Psychiatric',

    /**
     * Service areas
     *
     * These describe the geographic areas the clinic serves.
     * Do not use Pimple Saudagar as a City entity.
     */
    areaServed: [
      {
        '@type': 'Place',
        name: 'Pimple Saudagar'
      },
      {
        '@type': 'City',
        name: 'Pimpri-Chinchwad'
      },
      {
        '@type': 'City',
        name: 'Pune'
      }
    ],

    /**
     * Clinic hours:
     * Monday-Saturday
     * Morning: 10:00 AM - 12:00 PM
     * Evening: 05:00 PM - 08:00 PM
     */
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday'
        ],
        opens: '10:00',
        closes: '12:00'
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday'
        ],
        opens: '17:00',
        closes: '20:00'
      }
    ],

    /**
     * Google Maps / Google Business Profile map
     */
    hasMap: `https://www.google.com/maps/place/?q=place_id:${GOOGLE_MAPS_CONFIG.placeId}`,

    /**
     * Official social profiles
     */
    sameAs: [
      'https://facebook.com/manomay.homeo',
      'https://youtube.com/channel/UC1yNCpQ0hAe9tuKUgGTNUYg',
      'https://instagram.com/manomay.homeo/'
    ],

    /**
     * Founder
     */
    founder: {
      '@type': 'Person',
      '@id': `${CLINIC_URL}#dr-samiksha-murkute`,
      name: 'Dr. Samiksha Murkute',
      jobTitle: 'Homeopathic Psychiatrist',
      hasOccupation: {
        '@type': 'Occupation',
        name: 'Psychiatrist'
      },
      worksFor: {
        '@id': CLINIC_ID
      },
      url: `${CLINIC_URL}#dr-samiksha-murkute`
    }
  };

  return (
    <Helmet>
      {/* Primary SEO */}
      <title>{title}</title>
      <meta name="description" content={description} />

      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={CLINIC_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta
        property="og:image:alt"
        content={`${CLINIC_NAME} - Pimple Saudagar, Pune`}
      />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}